/**
 * API client for the Luminate dashboards.
 *
 * Mirrors the function surface of the standalone dashboard's api/client.js so
 * the ported pages work unchanged, but targets this backend's /api/luminate/*
 * routes and sends the JWT the rest of the app uses.
 *
 * Route mapping vs the reference API:
 *   /api/summaries/luminate               -> /api/luminate/tracks
 *   /api/summaries/luminate-agg           -> /api/luminate/agg
 *   /api/summaries/luminate-artists       -> /api/luminate/artists
 *   /api/summaries/luminate/track-history -> /api/luminate/track-history
 *   /api/mappings/luminate                -> /api/luminate/mapping
 *   /api/mappings/p-line                  -> /api/luminate/p-line
 *   /api/status/luminate                  -> /api/luminate/status
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchJson(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      // This backend returns {error}; the reference API returned {detail}.
      detail = body.error ?? body.detail ?? detail;
    } catch {
      // ignore parse errors
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  if (res.status === 204) return null;
  return res.json();
}

/** Shared discovery toggles -> query string (only non-default values are sent). */
function luminateDiscoveryQuery(filters = {}) {
  const params = new URLSearchParams();
  const map = {
    applyPLineIgnore: 'apply_p_line_ignore',
    applyAudioMin: 'apply_audio_min',
    applyWeeksMin: 'apply_weeks_min',
    applyTwRtdMax: 'apply_tw_rtd_max',
    applyPctChgMin: 'apply_pct_chg_min',
    applyResidualMin: 'apply_residual_min',
    applyFocusGenres: 'apply_focus_genres',
  };
  for (const [jsKey, apiKey] of Object.entries(map)) {
    if (filters[jsKey] === false) params.set(apiKey, '0');
  }
  if (filters.includeAll === true) params.set('include_all', '1');
  if (filters.offset != null && filters.offset > 0) {
    params.set('offset', String(filters.offset));
  }
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.weekEndDate) params.set('week_end_date', String(filters.weekEndDate));
  const q = params.toString();
  return q ? `?${q}` : '';
}

export function fetchLuminateSummary(filters = {}) {
  return fetchJson(`/api/luminate/tracks${luminateDiscoveryQuery(filters)}`);
}

export function fetchLuminateAggSummary(filters = {}) {
  return fetchJson(`/api/luminate/agg${luminateDiscoveryQuery(filters)}`);
}

export function fetchLuminateArtistsSummary(filters = {}) {
  const params = new URLSearchParams();
  const map = {
    applyWeeksUpMin: 'apply_weeks_up_min',
    applyFocusCountryLang: 'apply_focus_country_lang',
    applyFocusGenres: 'apply_focus_genres',
    applyAtdMax: 'apply_atd_max',
    applyPLineIgnore: 'apply_p_line_ignore',
  };
  for (const [jsKey, apiKey] of Object.entries(map)) {
    if (filters[jsKey] === false) params.set(apiKey, '0');
  }
  if (filters.includeAll === true) params.set('include_all', '1');
  if (filters.offset != null && filters.offset > 0) {
    params.set('offset', String(filters.offset));
  }
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.weekEndDate) params.set('week_end_date', String(filters.weekEndDate));
  const q = params.toString();
  return fetchJson(`/api/luminate/artists${q ? `?${q}` : ''}`);
}

export function fetchLuminateTrackHistory(songId) {
  const q = new URLSearchParams({ song_luminate_id: String(songId || '') });
  return fetchJson(`/api/luminate/track-history?${q}`);
}

export function fetchLuminateArtistHistory(artistId) {
  const q = new URLSearchParams({ artist_luminate_id: String(artistId || '') });
  return fetchJson(`/api/luminate/artist-history?${q}`);
}

export function fetchLuminateAggHistory({ primaryArtist } = {}) {
  const params = new URLSearchParams();
  if (primaryArtist) params.set('primary_artist', String(primaryArtist));
  return fetchJson(`/api/luminate/agg-history?${params}`);
}

export function fetchLuminateStatusMapping({ limit, entityType } = {}) {
  const params = new URLSearchParams();
  if (limit != null) params.set('limit', String(limit));
  if (entityType) params.set('entity_type', entityType);
  const q = params.toString();
  return fetchJson(`/api/luminate/mapping${q ? `?${q}` : ''}`);
}

/**
 * Write a Luminate status. `status` may be a tiered option ("Signed Artist"),
 * which carries its own tier, or a bare one ("Signed") with `entityType`.
 */
export function updateLuminateStatus({
  status,
  entityType,
  songLuminateId,
  primaryArtist,
  imprint,
  trackTitle,
  displayArtist,
  artistLuminateId,
  alsoUpdate,
}) {
  return fetchJson('/api/luminate/status', {
    method: 'PATCH',
    body: JSON.stringify({
      status: status === '' ? null : status,
      entity_type: entityType ?? null,
      song_luminate_id: songLuminateId ?? null,
      primary_artist: primaryArtist ?? null,
      imprint: imprint ?? null,
      track_title: trackTitle ?? null,
      display_artist: displayArtist ?? null,
      artist_luminate_id: artistLuminateId ?? null,
      also_update: alsoUpdate?.length ? alsoUpdate : null,
    }),
  });
}

/** Direct status-mapping edit from the Luminate Mapping page. */
export function updateLuminateStatusMapping({
  entityType,
  entityKey,
  status,
  title,
  primaryArtist,
} = {}) {
  return fetchJson('/api/luminate/mapping/status', {
    method: 'PATCH',
    body: JSON.stringify({
      entity_type: entityType,
      entity_key: entityKey,
      status,
      title: title ?? null,
      primary_artist: primaryArtist ?? null,
    }),
  });
}

export function deleteLuminateStatusMapping({ entityType, entityKey } = {}) {
  return fetchJson('/api/luminate/mapping', {
    method: 'DELETE',
    body: JSON.stringify({ entity_type: entityType, entity_key: entityKey }),
  });
}

/** Upload an Excel/CSV of statuses (multipart; Content-Type set by the browser). */
export async function uploadLuminateStatusBatch(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}/api/luminate/mapping/batch-status`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.error ?? body.detail ?? detail;
    } catch {
      // ignore
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json();
}

export function fetchPLineMapping(limit) {
  const q = limit != null ? `?limit=${encodeURIComponent(limit)}` : '';
  return fetchJson(`/api/luminate/p-line${q}`);
}

export function upsertPLineMapping({ text, partial } = {}) {
  return fetchJson('/api/luminate/p-line', {
    method: 'POST',
    body: JSON.stringify({ text, partial: Boolean(partial) }),
  });
}

export function deletePLineMapping({ text, id } = {}) {
  return fetchJson('/api/luminate/p-line', {
    method: 'DELETE',
    body: JSON.stringify({ text, id }),
  });
}
